"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { signOut } from "@/app/actions/auth";

interface Props {
  email: string;
  fullName: string | null;
}

export default function UserDropdown({ email, fullName }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const tc = useTranslations("common");
  const tu = useTranslations("userMenu");

  // Close on outside click + Escape.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // 1-letter initial for the avatar circle (uppercased).
  const initial = (fullName?.[0] ?? email[0] ?? "?").toUpperCase();

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 px-2 py-1 rounded-full hover:bg-ss-bg-soft cursor-pointer"
      >
        <span className="w-8 h-8 rounded-full bg-ss-blue text-white text-sm font-bold grid place-items-center">
          {initial}
        </span>
        <span className="text-sm font-semibold text-ss-text max-w-[140px] truncate">
          {fullName ?? email}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" className="text-ss-muted">
          <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute end-0 top-[calc(100%+8px)] w-56 bg-white border border-ss-line rounded-[14px] shadow-[var(--shadow-ss-xl)] py-2 z-[60] fade-up"
        >
          <div className="px-4 pt-1 pb-2 border-b border-ss-line">
            <div className="text-sm font-semibold text-ss-text truncate">
              {fullName ?? tu("signedIn")}
            </div>
            <div className="text-[12px] text-ss-muted truncate">{email}</div>
          </div>
          <MenuLink href="/account" label={tc("account")} onClick={() => setOpen(false)} />
          <MenuLink href="/account/bookings" label={tc("trips")} onClick={() => setOpen(false)} />
          <MenuLink href="/account/wishlist" label={tc("wishlist")} onClick={() => setOpen(false)} />
          <MenuLink href="/account/profile" label={tc("profile")} onClick={() => setOpen(false)} />
          <form action={signOut} className="border-t border-ss-line mt-1 pt-1">
            <button
              type="submit"
              className="w-full text-start px-4 py-2 text-sm text-ss-text hover:bg-ss-bg-soft cursor-pointer"
            >
              {tc("signOut")}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function MenuLink({ href, label, onClick }: { href: string; label: string; onClick: () => void }) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="block px-4 py-2 text-sm text-ss-text hover:bg-ss-bg-soft"
      role="menuitem"
    >
      {label}
    </Link>
  );
}
